import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

/**
 * CL-DISC-01 D5 — "You saved $X", X = original item total − REVISED item total.
 *
 * Negotiation and discount together, because that is what the buyer actually saved: a $100
 * discount on a $2,900 counter off a $3,650 list is $750 off, not $100 off. Shown only when
 * positive (D5) — a zero or negative "saving" is not a saving, and a seller-initiated offer at
 * list price with no discount has nothing to claim.
 *
 * Customer only. The same arithmetic on the seller's panel would read as money they lost.
 *
 * D13 (CL-NEG-01 post-merge, 2026-09-09) removed the dedicated discount's ENTRY POINTS, but NOT
 * the `negotiation.discountInSubunits` read below — a pre-D13 transaction's stored discount still
 * has to count toward "what the buyer saved". Deliberately parked for a future standalone
 * discount feature — do not delete it as dead code just because no NEW transaction sets it.
 *
 * @component
 * @param {Object} props
 * @param {Object} [props.negotiation] - `transaction.attributes.protectedData.negotiation`
 * @param {boolean} props.isCustomer
 * @param {string} props.currency
 * @param {Object} props.intl
 * @returns {JSX.Element|null}
 */
const LineItemOfferSavingsMaybe = props => {
  const { negotiation, isCustomer, currency, intl } = props;
  if (!isCustomer || !negotiation) return null; // EC13: no negotiation key on a BIN order.

  const original = negotiation.originalItemTotalInSubunits;
  const revised = negotiation.negotiatedItemTotalInSubunits - (negotiation.discountInSubunits || 0);
  if (!Number.isInteger(original) || !Number.isInteger(revised)) return null;

  const savedInSubunits = original - revised;
  if (savedInSubunits <= 0) return null;

  return (
    <div className={css.lineItem}>
      <span className={css.itemLabel}>
        <FormattedMessage
          id="OrderBreakdown.youSaved"
          values={{ amount: formatMoney(intl, new Money(savedInSubunits, currency)) }}
        />
      </span>
    </div>
  );
};

export default LineItemOfferSavingsMaybe;
